import { useEffect, useRef, useState } from "react";
import { createGlobalStyle } from "styled-components";
import { hexToRGB } from "@shared/utils/misc";
import { getNewLookTokens } from "common/NewLook/tokens.ts";

const FLASH_MS = 900;

/**
 * Live values (PnL from the socket) briefly tint when they change, green when
 * they rise and red when they fall, so an update is noticed without motion.
 * Returns the class to put on the value.
 */
const useValueFlash = (value: number | undefined) => {
  const previous = useRef(value);
  const [flash, setFlash] = useState<string>();
  useEffect(() => {
    const before = previous.current;
    previous.current = value;
    if (before === undefined || value === undefined || before === value) return;
    setFlash(value > before ? "nl-flash-up" : "nl-flash-down");
    const timeout = setTimeout(() => setFlash(undefined), FLASH_MS);
    return () => clearTimeout(timeout);
  }, [value]);
  return flash;
};

export default useValueFlash;

/** The flash classes; rendered once by the app in the new look. */
export const ValueFlashStyle = createGlobalStyle`
  @keyframes nl-flash-up {
    from { background-color: ${(p) =>
      hexToRGB(getNewLookTokens(p.theme).up, 0.16)}; }
    to { background-color: transparent; }
  }
  @keyframes nl-flash-down {
    from { background-color: ${(p) =>
      hexToRGB(getNewLookTokens(p.theme).down, 0.16)}; }
    to { background-color: transparent; }
  }
  .nl-flash-up {
    animation: nl-flash-up ${FLASH_MS}ms ease-out;
  }
  .nl-flash-down {
    animation: nl-flash-down ${FLASH_MS}ms ease-out;
  }
  @media (prefers-reduced-motion: reduce) {
    .nl-flash-up,
    .nl-flash-down {
      animation: none;
    }
  }
`;
